import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../context/AuthContext.js';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

export default function News() {
  // News state
  const [newsItems, setNewsItems] = useState([]);
  const [newNewsItem, setNewNewsItem] = useState({
    title: '',
    content: '',
    published: false
  });
  const [editingNewsId, setEditingNewsId] = useState(null);
  const [editingNews, setEditingNews] = useState(null);

  useEffect(() => {
    loadNewsItems();
  }, []);

  // News management functions
  const loadNewsItems = async () => {
    try {
      const response = await axiosInstance.get('/settings/news-items');
      setNewsItems(response.data);
    } catch (error) {
      console.error('Error loading news items:', error);
    }
  };

  const addNewsItem = async () => {
    if (!newNewsItem.title.trim() || !newNewsItem.content.trim()) {
      alert('Please enter both title and content');
      return;
    }

    try {
      const response = await axiosInstance.post('/settings/news-items', newNewsItem);
      setNewsItems([response.data, ...newsItems]);
      
      // Reset form
      setNewNewsItem({
        title: '',
        content: '',
        published: false
      });
    } catch (error) {
      console.error('Error adding news item:', error);
      alert('Failed to add news item');
    }
  };

  const startEditNews = (item) => {
    setEditingNewsId(item._id);
    setEditingNews({ ...item });
  };

  const saveEditNews = async () => {
    try {
      const response = await axiosInstance.patch(`/settings/news-items/${editingNewsId}`, {
        title: editingNews.title,
        content: editingNews.content,
        published: editingNews.published
      });
      
      const updatedItems = newsItems.map(item =>
        item._id === editingNewsId ? response.data : item
      );
      setNewsItems(updatedItems);
      setEditingNewsId(null);
      setEditingNews(null);
    } catch (error) {
      console.error('Error updating news item:', error);
      alert('Failed to update news item');
    }
  };

  const cancelEditNews = () => {
    setEditingNewsId(null);
    setEditingNews(null);
  };

  const deleteNewsItem = async (id) => {
    if (window.confirm('Are you sure you want to delete this news item?')) {
      try {
        await axiosInstance.delete(`/settings/news-items/${id}`);
        const updatedItems = newsItems.filter(item => item._id !== id);
        setNewsItems(updatedItems);
      } catch (error) {
        console.error('Error deleting news item:', error);
        alert('Failed to delete news item');
      }
    }
  };

  const quillModules = {
    toolbar: [
      [{ 'header': [1, 2, 3, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      ['link', 'image'],
      ['clean']
    ]
  };

  return (
    <div className="font-['Montserrat']">
      {/* Add New News Item Form */}
      <div className="border border-gray-200 rounded-lg p-4 mb-6 bg-gray-50">
        <h4 className="text-lg font-semibold mb-3">Add New News Article</h4>
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <input
              type="text"
              value={newNewsItem.title}
              onChange={(e) => setNewNewsItem({ ...newNewsItem, title: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Enter news title"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Content *</label>
            <ReactQuill
              theme="snow"
              value={newNewsItem.content}
              onChange={(content) => setNewNewsItem({ ...newNewsItem, content })}
              className="bg-white"
              placeholder="Write your news content here..."
              modules={quillModules}
            />
          </div>
          <div className="flex items-center">
            <input
              type="checkbox"
              checked={newNewsItem.published}
              onChange={(e) => setNewNewsItem({ ...newNewsItem, published: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              id="publish-new"
            />
            <label htmlFor="publish-new" className="ml-2 text-sm font-medium text-gray-700">
              Publish immediately
            </label>
          </div>
        </div>
        <button
          onClick={addNewsItem}
          className="mt-4 px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition duration-200 font-semibold"
        >
          Add News Article
        </button>
      </div>

      {/* News Items List */}
      <div>
        <h4 className="text-lg font-semibold mb-3">News Articles ({newsItems.length})</h4>
        {newsItems.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <p>No news articles yet. Add your first article above!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {newsItems.map((item) => (
              <div key={item._id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition duration-200">
                {editingNewsId === item._id ? (
                  // Edit Mode
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Title</label>
                      <input
                        type="text"
                        value={editingNews.title}
                        onChange={(e) => setEditingNews({ ...editingNews, title: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">Content</label>
                      <ReactQuill
                        theme="snow"
                        value={editingNews.content}
                        onChange={(content) => setEditingNews({ ...editingNews, content })}
                        className="bg-white"
                        modules={quillModules}
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editingNews.published}
                        onChange={(e) => setEditingNews({ ...editingNews, published: e.target.checked })}
                        className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                        id={`publish-edit-${item._id}`}
                      />
                      <label htmlFor={`publish-edit-${item._id}`} className="ml-2 text-xs font-medium text-gray-700">
                        Published
                      </label>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={saveEditNews}
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 transition"
                      >
                        Save
                      </button>
                      <button
                        onClick={cancelEditNews}
                        className="px-4 py-2 bg-gray-300 text-gray-700 rounded hover:bg-gray-400 transition"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <div>
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-grow">
                        <div className="flex items-center gap-3 mb-1">
                          <h5 className="text-lg font-semibold text-gray-800">{item.title}</h5>
                          {item.published ? (
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-semibold">
                              Published
                            </span>
                          ) : (
                            <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-semibold">
                              Draft
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <div 
                      className="text-gray-600 mb-3 prose prose-sm max-w-none"
                      dangerouslySetInnerHTML={{ __html: item.content }}
                    />
                    <div className="flex justify-between items-center">
                      <span className="text-xs text-gray-400">
                        {item.publishDate ? `Published: ${new Date(item.publishDate).toLocaleDateString()}` : `Created: ${new Date(item.createdAt).toLocaleDateString()}`}
                      </span>
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditNews(item)}
                          className="px-3 py-1 text-sm bg-blue-500 text-white rounded hover:bg-blue-600 transition"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deleteNewsItem(item._id)}
                          className="px-3 py-1 text-sm bg-red-500 text-white rounded hover:bg-red-600 transition"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

